/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	forwardRef,
	type Ref,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { bindAll } from 'bind-event-listener';
import { defineMessages, useIntl } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import { cssMap, jsx } from '@atlaskit/css';
import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import {
	type ConversationAction,
	HandleActionType,
} from '@atlassian/rovo-conversation-actions/types';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api/rovo-experience-api-store';

import { useAISmartButtonContext } from '../../../../common/context';
import { useDailyBriefSync } from '../../../../common/hooks/use-daily-brief-sync';
import { useUnreadConversationStatus } from '../../../../common/hooks/use-unread-conversation-status';
import type { AISmartButtonProps } from '../../../../common/types/shared-types';
import { isBrowser } from '../../../../common/utils/is-browser';
import { useRovoButtonEventsInstrumentation } from '../../../../hooks/useRovoButtonEventsInstrumentation';
import { AIActionShortcutMenu } from '../../../ai-action-shortcut-menu';
import { AIButton } from '../../../ai-button';
import { AIProactiveNudge } from '../../../ai-proactive-nudge';
import { GlowAnimation } from '../glow-animation';
import { QuickInput } from './quick-input';
import { ContinueInChat } from './quick-input/continue-in-chat';
import { RovoButtonExpander } from './rovo-button-expander';

type QuickInputAnalyticsAction = 'clicked' | 'expanded' | 'started' | 'submitted';
type QuickInputAnalyticsSubjectId = 'aiRovoQuickInput' | 'aiRovoQuickInputContinueInChat';
type ConversationStatusAnalyticsAction = 'clicked' | 'expanded';
type ConversationStatusDestination = 'conversation' | 'history';

const i18n = defineMessages({
	tooltipLabel: {
		id: 'ai-smart-button.tooltip.label.v3',
		defaultMessage: 'Ask Rovo anything',
		description: 'Label for the AI smart button tooltip',
	},
	tooltipUnreadSuffix: {
		id: 'ai-smart-button.tooltip.unread-suffix.v2',
		defaultMessage: '{count, plural, one {# unread chat} other {# unread chats}}',
		description:
			'Suffix appended to the tooltip when there are unread conversations, e.g. "3 unread chats"',
	},
	tooltipInProgress: {
		id: 'ai-smart-button.tooltip.in-progress.v2',
		defaultMessage: '{count, plural, one {# task in progress} other {# tasks in progress}}',
		description: 'Label shown when there are in-progress tasks, e.g. "1 task in progress"',
	},
	tooltipAwaitingInput: {
		id: 'ai-smart-button.tooltip.awaiting-input.v3',
		defaultMessage: '{count, plural, one {# task needs input} other {# tasks need input}}',
		description:
			'Label shown when one or more Rovo tasks are blocked waiting on the user to respond, e.g. "2 tasks need input"',
	},
});

const styles = cssMap({
	buttonWrapper: {
		position: 'relative',
	},
	tooltipContent: {
		textAlign: 'left',
	},
	expandPillInteractive: {
		appearance: 'none',
		border: 'none',
		cursor: 'pointer',
		backgroundColor: 'transparent',
		display: 'flex',
		alignItems: 'center',
		font: token('font.body.small'),
		color: token('color.text.inverse'),
		'&:hover': {
			// @ts-expect-error TS2322 opacity 0.9 not in allowed opacity union (0 | 1 | tokens)
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			opacity: 0.9,
		},
	},
	// Inner column so the 2-line label fits within the 48px pill height.
	expandPillInner: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'flex-end',
		whiteSpace: 'nowrap',
		font: token('font.body.small'),
		color: token('color.text.inverse'),
	},
	expandedPillContent: {
		color: token('color.text.inverse'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'start',
		paddingInline: token('space.150'),
	},
});

type AISmartButtonUIProps = Pick<AISmartButtonProps, 'isDisabled'> & {
	onChatHistoryClick?: () => void;
	onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	onQuickInputContinueInChat?: (prompt: string) => void;
	onQuickInputSubmit?: (prompt: string) => void;
	onUnreadConversationClick?: (conversationId: string) => void;
	/**
	 * Mouse-proximity animation key (TREX-1527). Bumped by AISmartButton when
	 * the cursor enters the proximity radius; remounting the animated logo
	 * restarts its SMIL color-cycle.
	 */
	proximityAnimationKey?: number;
};

export const AISmartButtonUI: React.ForwardRefExoticComponent<
	Pick<AISmartButtonProps, 'isDisabled'> & {
		onChatHistoryClick?: () => void;
		onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
		onMouseEnter: () => void;
		onMouseLeave: () => void;
		onQuickInputContinueInChat?: (prompt: string) => void;
		onQuickInputSubmit?: (prompt: string) => void;
		onUnreadConversationClick?: (conversationId: string) => void;
		proximityAnimationKey?: number;
	} & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, AISmartButtonUIProps>(
	(
		{
			onClick,
			isDisabled,
			onMouseEnter,
			onMouseLeave,
			onQuickInputContinueInChat,
			onQuickInputSubmit,
			proximityAnimationKey,
			onChatHistoryClick,
			onUnreadConversationClick,
		},
		ref: Ref<HTMLButtonElement>,
	) => {
		const {
			sidebarApi,
			analyticsAttributes,
			product,
			productPage,
			getActions,
			onAnchorElementReady,
		} = useAISmartButtonContext();

		const isUnreadIndicatorEnabled = isExperimentEnabled('rovo_button_unread_indicator');
		const isDailyBriefEnabled = fg('rovo_button_daily_brief_nudge');
		const isQuickInputEnabled = isExperimentEnabled('rovo_button_quick_chat_input');
		const unreadStatusHookEnabled =
			isUnreadIndicatorEnabled || isDailyBriefEnabled || isQuickInputEnabled;
		const {
			hasUnread,
			unreadCount,
			inProgressCount,
			awaitingInputCount,
			mostRecentUnread,
			mostRecentInProgress,
			mostRecentAwaitingInput,
			isStatusReady,
		} = useUnreadConversationStatus({
			enabled: unreadStatusHookEnabled,
		});
		useDailyBriefSync({ enabled: isDailyBriefEnabled, mostRecentUnread });

		// Awaiting-input drives the button's needs-input icon. The expanded label can
		// show awaiting-input alongside unread or in-progress counts when multiple
		// conversation states are present.
		// (TREX-1891)
		const displayAwaitingInputCount = isUnreadIndicatorEnabled ? awaitingInputCount : 0;
		const displayUnreadCount = isUnreadIndicatorEnabled && hasUnread ? unreadCount : 0;
		const displayInProgressCount = isUnreadIndicatorEnabled ? inProgressCount : 0;
		const showAwaitingInput = displayAwaitingInputCount > 0;
		const showUnread = displayUnreadCount > 0;
		const hasVisibleConversationStatus =
			showUnread || displayInProgressCount > 0 || showAwaitingInput;
		const { formatMessage } = useIntl();
		const { toggleConversationAssistantUIMode } = useRovoExperienceAPIStore();
		const { fireRovoButtonUIEvent } = useRovoButtonEventsInstrumentation();
		const fireQuickInputUIEvent = useCallback(
			(
				action: QuickInputAnalyticsAction,
				actionSubjectId: QuickInputAnalyticsSubjectId = 'aiRovoQuickInput',
			) => {
				fireRovoButtonUIEvent({
					source: productPage,
					action,
					actionSubject: 'rovoButton',
					actionSubjectId,
					attributes: {
						product,
						productPage,
						...analyticsAttributes,
						version: 'refresh',
					},
					product,
				});
			},
			[analyticsAttributes, fireRovoButtonUIEvent, product, productPage],
		);
		const fireConversationStatusUIEvent = useCallback(
			(action: ConversationStatusAnalyticsAction, destination?: ConversationStatusDestination) => {
				fireRovoButtonUIEvent({
					source: productPage,
					action,
					actionSubject: 'rovoButton',
					actionSubjectId: 'aiRovoConversationStatus',
					attributes: {
						product,
						productPage,
						...analyticsAttributes,
						version: 'refresh',
						unreadCount: displayUnreadCount,
						inProgressCount: displayInProgressCount,
						awaitingInputCount: displayAwaitingInputCount,
						totalConversationCount:
							displayUnreadCount + displayInProgressCount + displayAwaitingInputCount,
						...(destination ? { destination } : {}),
					},
					product,
				});
			},
			[
				analyticsAttributes,
				displayAwaitingInputCount,
				displayInProgressCount,
				displayUnreadCount,
				fireRovoButtonUIEvent,
				product,
				productPage,
			],
		);
		const handleQuickInputStarted = useCallback(() => {
			fireQuickInputUIEvent('started');
		}, [fireQuickInputUIEvent]);
		const handleQuickInputSubmit = useCallback(
			(prompt: string) => {
				fireQuickInputUIEvent('submitted');
				onQuickInputSubmit?.(prompt);
			},
			[fireQuickInputUIEvent, onQuickInputSubmit],
		);
		const handleQuickInputContinueInChat = useCallback(
			(prompt: string) => {
				fireQuickInputUIEvent('clicked', 'aiRovoQuickInputContinueInChat');
				onQuickInputContinueInChat?.(prompt);
			},
			[fireQuickInputUIEvent, onQuickInputContinueInChat],
		);

		const [canShowNudge, setCanShowNudge] = useState(false);
		const [spotlightRendered, setSpotlightRendered] = useState(false);
		const [hasActiveNudge, setHasActiveNudge] = useState(false);
		const nudgeCounterRef = useRef(0);
		const handleActiveNudgeChange = useCallback((active: boolean) => {
			setHasActiveNudge((prev) => {
				if (active && !prev) {
					nudgeCounterRef.current += 1;
				}
				return active;
			});
		}, []);
		const [isHovering, setIsHovering] = useState(false);
		const [isExpanded, setIsExpanded] = useState(false);
		const quickInputPromptRef = useRef('');
		const [isQuickInputOverflowing, setIsQuickInputOverflowing] = useState(false);
		// Only wait for conversation status when its indicator is enabled. Hidden
		// statuses must not precondition the quick-input experiment.
		const isConversationStatusPending = isUnreadIndicatorEnabled && !isStatusReady;
		const canShowQuickInput =
			isQuickInputEnabled &&
			!isConversationStatusPending &&
			!hasVisibleConversationStatus &&
			!hasActiveNudge;
		const wasQuickInputExpandedRef = useRef(false);
		const wasConversationStatusExpandedRef = useRef(false);
		const handleExpandedChange = useCallback(
			(expanded: boolean) => {
				setIsExpanded(expanded);
				const isQuickInputExpanded = expanded && canShowQuickInput;
				if (isQuickInputExpanded && !wasQuickInputExpandedRef.current) {
					fireQuickInputUIEvent('expanded');
				}
				wasQuickInputExpandedRef.current = isQuickInputExpanded;

				const isConversationStatusExpanded = expanded && hasVisibleConversationStatus;
				if (isConversationStatusExpanded && !wasConversationStatusExpandedRef.current) {
					fireConversationStatusUIEvent('expanded');
				}
				wasConversationStatusExpandedRef.current = isConversationStatusExpanded;
			},
			[
				canShowQuickInput,
				fireConversationStatusUIEvent,
				fireQuickInputUIEvent,
				hasVisibleConversationStatus,
			],
		);
		const handleQuickInputPromptChange = useCallback((prompt: string) => {
			quickInputPromptRef.current = prompt;
		}, []);
		const [actionShortcuts, setActionShortcuts] = useState<ConversationAction[]>([]);
		const placementElementRef = useRef<HTMLDivElement | null>(null);
		const notifiedSpotlightAnchorIdRef = useRef<string>();
		const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

		const onboardingSpotlightElementId = useMemo(() => {
			const isLiveOrEditPage =
				analyticsAttributes?.pageMode === 'live' || analyticsAttributes?.pageMode === 'edit';
			const isPageOrBlogpost = productPage === 'page' || productPage === 'blogpost';

			if (isPageOrBlogpost && isLiveOrEditPage) {
				return `rovo-button-onboarding-spotlight-live-or-edit`;
			}

			return `rovo-button-onboarding-spotlight`;
		}, [productPage, analyticsAttributes]);

		useEffect(() => {
			const placementElement = placementElementRef.current;

			if (
				!placementElement?.isConnected ||
				placementElement.id !== onboardingSpotlightElementId ||
				notifiedSpotlightAnchorIdRef.current === onboardingSpotlightElementId
			) {
				return;
			}

			notifiedSpotlightAnchorIdRef.current = onboardingSpotlightElementId;
			onAnchorElementReady?.(onboardingSpotlightElementId);
		}, [onAnchorElementReady, onboardingSpotlightElementId]);

		// Fetch action shortcuts when experiment is enabled
		const slaOperationsEnabled = fg('jmb-4989_add_sla_operations_in_rovo');
		useEffect(() => {
			const shouldFetch = getActions && product === 'jira' && slaOperationsEnabled;

			if (shouldFetch) {
				getActions()
					.then((actions) => {
						// Filter to only get actions that can be used as shortcuts (Base, Chat, or Skill e.g. Jira SLA when feature-flagged)
						const shortcuts = actions.filter(
							(action) =>
								action.handleType === HandleActionType.Base ||
								action.handleType === HandleActionType.Chat ||
								(slaOperationsEnabled && action.handleType === HandleActionType.Skill),
						);
						const slicedShortcuts = shortcuts.slice(0, 3);
						setActionShortcuts(slicedShortcuts);
					})
					.catch(() => {
						setActionShortcuts([]);
					});
			}
		}, [getActions, product, slaOperationsEnabled]);

		// Handle "Get Started" button click from spotlight
		const handleGetStartedClick = useCallback(() => {
			if (sidebarApi) {
				toggleConversationAssistantUIMode({
					targetMode: 'mini-modal',
					sidebarApi,
				});
			}
		}, [sidebarApi, toggleConversationAssistantUIMode]);

		// Set up event listeners for spotlight communication
		useEffect(() => {
			if (
				!isBrowser() ||
				!placementElementRef.current ||
				!fg('cc_ai_growth_welcome_from_rovo_gate')
			) {
				return;
			}

			const doc = getDocument();

			if (!doc) {
				return;
			}

			// Listen for spotlight rendered event
			const handleSpotlightRendered = (event: Event) => {
				const customEvent = event as CustomEvent;
				if (customEvent.detail?.elementId === onboardingSpotlightElementId) {
					setSpotlightRendered(true);
				}
			};

			// Listen for "Get Started" button click event
			const handleGetStartedEvent = (event: Event) => {
				const customEvent = event as CustomEvent;
				if (customEvent.detail?.elementId === onboardingSpotlightElementId) {
					handleGetStartedClick();
				}
			};

			const unbind = bindAll(doc, [
				{
					type: 'rovo-button-onboarding-spotlight-rendered',
					listener: handleSpotlightRendered,
				},
				{
					type: 'rovo-button-onboarding-spotlight-get-started-click',
					listener: handleGetStartedEvent,
				},
			]);

			return () => {
				unbind();
			};
		}, [handleGetStartedClick, onboardingSpotlightElementId]);

		useEffect(() => {
			if (spotlightRendered) {
				return;
			}

			if (!fg('cc_ai_growth_welcome_from_rovo_gate') && !canShowNudge) {
				setCanShowNudge(true);
				return;
			}

			// Give spotlight time to render (if it will) since it needs to run decisions logic
			const timeout = setTimeout(() => {
				if (!spotlightRendered && !canShowNudge) {
					setCanShowNudge(true);
				}
			}, 1000);

			return () => clearTimeout(timeout);
		}, [spotlightRendered, canShowNudge]);

		/* eslint-disable @atlaskit/platform/no-preconditioning -- intentional gate + experiment layering */
		const nudgeOrPlacement = useMemo(() => {
			return (
				<React.Fragment>
					{fg('cc_ai_growth_welcome_from_rovo_gate') && (
						<div id={onboardingSpotlightElementId} ref={placementElementRef} />
					)}
					{canShowNudge && (hasActiveNudge || !isExpanded) && (
						<AIProactiveNudge onActiveNudgeChange={handleActiveNudgeChange} />
					)}
				</React.Fragment>
			);
		}, [
			canShowNudge,
			handleActiveNudgeChange,
			onboardingSpotlightElementId,
			hasActiveNudge,
			isExpanded,
		]);
		/* eslint-enable @atlaskit/platform/no-preconditioning */

		const handleButtonMouseEnter = useCallback(() => {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = setTimeout(() => {
				setIsHovering(true);
			}, 250);
			void import('@atlassian/conversation-assistant/preload-chat-on-intent')
				.then((m) => m.preloadChatOnIntent())
				.catch(() => {});
			onMouseEnter();
		}, [onMouseEnter]);

		const handleButtonMouseLeave = useCallback(() => {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = setTimeout(() => {
				setIsHovering(false);
				onMouseLeave();
			}, 1000);
		}, [onMouseLeave]);

		const handleActionBoxMouseEnter = useCallback(() => {
			clearTimeout(hoverTimeoutRef.current);
			setIsHovering(true);
		}, []);

		const handleActionBoxMouseLeave = useCallback(() => {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = setTimeout(() => {
				setIsHovering(false);
			}, 1000);
		}, []);

		const canShowActionShortcuts = useMemo(() => {
			const jiraWithShortcuts =
				slaOperationsEnabled && product === 'jira' && actionShortcuts.length > 0;
			return (
				actionShortcuts.length > 0 && !hasActiveNudge && !spotlightRendered && jiraWithShortcuts
			);
		}, [actionShortcuts.length, hasActiveNudge, spotlightRendered, product, slaOperationsEnabled]);

		const actionShortcutsRendering = useMemo(() => {
			if (!isHovering || !canShowActionShortcuts) {
				return null;
			}

			return (
				<AIActionShortcutMenu
					actionShortcuts={actionShortcuts}
					onMouseEnter={handleActionBoxMouseEnter}
					onMouseLeave={handleActionBoxMouseLeave}
				/>
			);
		}, [
			isHovering,
			canShowActionShortcuts,
			actionShortcuts,
			handleActionBoxMouseEnter,
			handleActionBoxMouseLeave,
		]);

		const defaultLabel = formatMessage(i18n.tooltipLabel);
		const awaitingInputLabel = showAwaitingInput
			? formatMessage(i18n.tooltipAwaitingInput, { count: displayAwaitingInputCount })
			: null;
		const unreadLabel = showUnread
			? formatMessage(i18n.tooltipUnreadSuffix, { count: displayUnreadCount })
			: null;
		const inProgressLabel =
			displayInProgressCount > 0
				? formatMessage(i18n.tooltipInProgress, { count: displayInProgressCount })
				: null;
		const labelLines: Array<{ key: string; label: string }> = (() => {
			if (awaitingInputLabel && unreadLabel) {
				return [
					{ key: 'awaiting-input', label: awaitingInputLabel },
					{ key: 'unread', label: unreadLabel },
				];
			}
			if (awaitingInputLabel && inProgressLabel) {
				return [
					{ key: 'awaiting-input', label: awaitingInputLabel },
					{ key: 'in-progress', label: inProgressLabel },
				];
			}
			if (awaitingInputLabel) {
				return [{ key: 'awaiting-input', label: awaitingInputLabel }];
			}
			if (unreadLabel && inProgressLabel) {
				return [
					{ key: 'unread', label: unreadLabel },
					{ key: 'in-progress', label: inProgressLabel },
				];
			}
			if (unreadLabel) {
				return [{ key: 'unread', label: unreadLabel }];
			}
			if (inProgressLabel) {
				return [{ key: 'in-progress', label: inProgressLabel }];
			}
			return [{ key: 'default', label: defaultLabel }];
		})();
		const labelContent = (
			<Box xcss={styles.tooltipContent}>
				{labelLines.map((line) => (
					<Box key={line.key}>{line.label}</Box>
				))}
			</Box>
		);

		// The visible inline label is aria-hidden, so provide an SR-only text node
		// carrying the same awaiting/unread/in-progress information.
		const accessibleLabelText = labelLines.map((line) => line.label).join('. ');
		const shouldUseExpander = isUnreadIndicatorEnabled || isQuickInputEnabled;
		const isExpansionSuppressed =
			hasActiveNudge || (isConversationStatusPending && !hasVisibleConversationStatus);

		// Decide between deep-linking to a single chat vs opening chat history. We
		// deep-link only when the visible conversation-status counts add up to one.
		// Any total of two or more opens the history list instead. (TREX-1891 / TREX-1894)
		const handleConversationStatusClick = useCallback(() => {
			const signals = [
				{ count: displayAwaitingInputCount, conversationId: mostRecentAwaitingInput?.id },
				{ count: displayUnreadCount, conversationId: mostRecentUnread?.id },
				{ count: displayInProgressCount, conversationId: mostRecentInProgress?.id },
			].filter((signal) => signal.count > 0);

			const totalConversationCount = signals.reduce((total, signal) => total + signal.count, 0);
			const singleConversationId =
				totalConversationCount === 1 ? signals[0]?.conversationId : undefined;
			fireConversationStatusUIEvent('clicked', singleConversationId ? 'conversation' : 'history');

			if (singleConversationId) {
				if (onUnreadConversationClick) {
					onUnreadConversationClick(singleConversationId);
					return;
				}
			}
			onChatHistoryClick?.();
		}, [
			displayAwaitingInputCount,
			mostRecentAwaitingInput,
			displayUnreadCount,
			mostRecentUnread,
			displayInProgressCount,
			mostRecentInProgress,
			fireConversationStatusUIEvent,
			onUnreadConversationClick,
			onChatHistoryClick,
		]);

		const conversationStatusContent = (
			<Pressable
				testId="rovo-button-expanded-click"
				xcss={styles.expandPillInteractive}
				data-rovo-expand-pill
				aria-label={accessibleLabelText}
				onClick={handleConversationStatusClick}
			>
				<Box xcss={styles.expandedPillContent}>
					<Box xcss={styles.expandPillInner}>{labelContent}</Box>
				</Box>
			</Pressable>
		);

		const idleExpandedContent = (
			<Pressable
				testId="rovo-button-expanded-click"
				xcss={styles.expandPillInteractive}
				data-rovo-expand-pill
				aria-label={accessibleLabelText}
				onClick={onClick}
				isDisabled={isDisabled}
			>
				<Box xcss={styles.expandedPillContent}>
					<Box xcss={styles.expandPillInner}>{labelContent}</Box>
				</Box>
			</Pressable>
		);

		const expandedContent = hasVisibleConversationStatus ? (
			conversationStatusContent
		) : canShowQuickInput ? (
			<QuickInput
				onOverflowChange={setIsQuickInputOverflowing}
				onPromptChange={handleQuickInputPromptChange}
				onStart={handleQuickInputStarted}
				onSubmit={handleQuickInputSubmit}
			/>
		) : (
			idleExpandedContent
		);

		return (
			<Inline space="space.100" alignBlock="center" xcss={styles.buttonWrapper}>
				{nudgeOrPlacement}
				{shouldUseExpander ? (
					<RovoButtonExpander
						isExpansionSuppressed={isExpansionSuppressed}
						onExpandedChange={handleExpandedChange}
						overlayContent={
							canShowQuickInput && isQuickInputOverflowing ? (
								<ContinueInChat
									onClick={() => handleQuickInputContinueInChat(quickInputPromptRef.current.trim())}
								/>
							) : undefined
						}
						expandedContent={expandedContent}
					>
						<AIButton
							onClick={onClick}
							isDisabled={isDisabled}
							hasUnread={showUnread && !isExpanded}
							isAwaitingInput={showAwaitingInput && !isExpanded}
							onMouseEnter={handleButtonMouseEnter}
							onMouseLeave={handleButtonMouseLeave}
							ref={ref}
							proximityAnimationKey={proximityAnimationKey}
						/>
					</RovoButtonExpander>
				) : (
					<Tooltip
						position="left"
						content={
							<Box xcss={styles.tooltipContent}>
								<Box>
									{showUnread
										? `${formatMessage(i18n.tooltipLabel)} • ${formatMessage(
												i18n.tooltipUnreadSuffix,
												{ count: displayUnreadCount },
											)}`
										: formatMessage(i18n.tooltipLabel)}
								</Box>
								{displayInProgressCount > 0 && (
									<Box>
										{formatMessage(i18n.tooltipInProgress, { count: displayInProgressCount })}
									</Box>
								)}
							</Box>
						}
						hideTooltipOnClick
						ignoreTooltipPointerEvents
						canAppear={() => {
							// If control or not on editor surface, always show tooltip when no active nudge
							// If treatment and on editor surface, show tooltip only when shortcuts cannot be shown
							return !hasActiveNudge && !canShowActionShortcuts;
						}}
					>
						<AIButton
							onClick={onClick}
							isDisabled={isDisabled}
							hasUnread={showUnread}
							onMouseEnter={handleButtonMouseEnter}
							onMouseLeave={handleButtonMouseLeave}
							ref={ref}
							proximityAnimationKey={proximityAnimationKey}
						/>
					</Tooltip>
				)}
				{hasActiveNudge && <GlowAnimation key={nudgeCounterRef.current} />}
				<ExitingPersistence appear>{actionShortcutsRendering}</ExitingPersistence>
			</Inline>
		);
	},
);
